import { Injectable, NgZone } from '@angular/core'
import { Observable, of } from 'rxjs'
import { finalize, switchMap, tap } from 'rxjs/operators'
import * as lodash from 'lodash'

import { Rental, RateView, RentalRates, RateService, Channel, tokeetDashboardChannel } from '@tokeet/tv3-platform'
import { RatePipe } from '@tv3/pipes/utility/rate.pipe'
import { MonthDayView } from './rate.view'

@Injectable()
export class RatesService {
  fetching = true
  fetched = false

  channels: Record<string, Channel> = {}

  private data = new Map<number, MonthDayView>()
  private defaultView: MonthDayView

  constructor(private ratePipe: RatePipe, private rateService: RateService, private zone: NgZone) {}

  setChannels(channels: Channel[]) {
    this.channels = lodash.keyBy(channels, (c) => c.id)
  }

  fetchRates(rental: Rental, refresh = false): Observable<any> {
    return of(null).pipe(
      tap(() => {
        if (refresh) {
          this.data = new Map<number, MonthDayView>()
          this.defaultView = null
          this.fetched = false
        }

        this.zone.run(() => {
          this.fetching = true
        })
      }),
      switchMap(() => this.rateService.all([rental.id])),
      tap((rates: any) => {
        this.updateResults(rental, rates)

        this.fetched = true
      }),
      finalize(() => {
        this.zone.run(() => {
          this.fetching = false
        })
      })
    )
  }

  getRate(date: number): MonthDayView {
    return this.data[date] || this.defaultView || null
  }

  private updateResults(rental: Rental, rates: RentalRates[]) {
    const rateViews = lodash.mapValues(this.rateService.getRateViews(rates), (data) => data[rental.id])
    let baseRateView = this.rateService.getBaseRateViews(rates)[rental.id]

    if (!baseRateView) {
      baseRateView = {
        price: 0,
        weekend: 0,
        category: null,
        currency: (rental.currency && rental.currency.code) || null,
        minimumStay: null,
        maximumStay: null,
        maxGuest: null,
        baseRate: true,
      }

      this.defaultView = null
    } else {
      const defaultView: MonthDayView = {
        rateView: baseRateView,
        rateViews: [],

        nights: baseRateView.minimumStay,

        rates: baseRateView.price
          ? [
              {
                label: 'Base Rate',
                price: this.ratePipe.transform(baseRateView.price, rental.currency),
                weekend: baseRateView.weekend && this.ratePipe.transform(baseRateView.weekend, rental.currency),
                baseRate: true,
              },
            ]
          : [],
        allRates: [],
      }

      defaultView.allRates = defaultView.rates

      this.defaultView = defaultView
    }

    const categories = lodash.keys(rateViews).filter((category) => category !== 'default')
    const dates = lodash.chain(rateViews).values().map(lodash.keys).flatten().uniq().value()

    dates.forEach((date) => {
      const dayView: MonthDayView = {
        rateView: null,
        rateViews: [],

        nights: 0,
        rates: [],
        allRates: [],
      }

      categories.forEach((category) => {
        const rateView: RateView = lodash.get(rateViews, [category, date])

        if (rateView) {
          const channel = this.channels[category]
          const categoryName = channel
            ? channel.friendlyName
            : category === tokeetDashboardChannel.id
            ? 'Tokeet Dashboard'
            : lodash.startCase(category)
          const price = this.ratePipe.transform(rateView.price, rental.currency)
          const weekend = rateView.weekend && this.ratePipe.transform(rateView.weekend, rental.currency)

          dayView.rateViews.push(rateView)
          dayView.rates.push({ label: categoryName, price, weekend, baseRate: false })
        }
      })

      const defaultRateView = rateViews['default'] && rateViews['default'][date]

      dayView.rateView = defaultRateView || baseRateView

      dayView.nights = dayView.rateView.minimumStay

      if (defaultRateView && defaultRateView.price) {
        dayView.rates.unshift({
          label: 'Default',
          price: this.ratePipe.transform(defaultRateView.price, rental.currency),
          weekend: defaultRateView.weekend && this.ratePipe.transform(defaultRateView.weekend, rental.currency),
          baseRate: defaultRateView.baseRate,
        })
      } else if (baseRateView.price) {
        dayView.rates.unshift({
          label: 'Base Rate',
          price: this.ratePipe.transform(baseRateView.price, rental.currency),
          weekend: baseRateView.weekend && this.ratePipe.transform(baseRateView.weekend, rental.currency),
          baseRate: true,
        })
      }

      dayView.allRates = dayView.rates
      dayView.rates = dayView.rates.slice(0, 4) // NOTE: show max 4 rates

      this.data[date] = dayView
    })
  }
}
